import React, { type JSX, useCallback, useContext, useMemo, useRef, memo, useState } from "react";
import { secureRandomString } from "matrix-js-sdk/src/randomstring";
import type * as maplibregl from "maplibre-gl";
import { EventType, IContent, MatrixEvent } from "matrix-js-sdk/src/matrix";

import { Map, LocationMapTile } from "../location";
import ZoomButtons from "../location/ZoomButtons";
import BaseDialog from "../dialogs/BaseDialog";
import { parseGeoUri } from "../../../utils/location";
import { createMarker } from "../../../utils/location/map";
import { type IBodyProps } from "./IBodyProps";
import { _t } from "../../../languageHandler";
import MatrixClientContext from "../../../contexts/MatrixClientContext";
import Modal from "../../../Modal";
import { useLocationMapError } from "../../../hooks/useLocationMapError";
import classNames from "classnames";
import { isVVF } from "../../../dth/appUtils";

export interface SOSMessageData {
    body: string;
    latitude: number | null;
    longitude: number | null;
}

/**
 * Try to parse a message body as an SOS JSON payload.
 * @param rawBody The raw message body to parse.
 * Returns the parsed data if valid, or null otherwise.
 */
export function tryParseSOS(rawBody: string): SOSMessageData | null {
    try {
        const parsed = JSON.parse(rawBody);
        if (
            parsed &&
            typeof parsed === "object" &&
            typeof parsed.body === "string" &&
            "latitude" in parsed &&
            "longitude" in parsed
        ) {
            // Parse latitude and longitude as numbers, set to null if invalid
            let latitude: number | null = null;
            let longitude: number | null = null;
            const lat = typeof parsed.latitude === "string" ? parseFloat(parsed.latitude) : parsed.latitude;
            const lon = typeof parsed.longitude === "string" ? parseFloat(parsed.longitude) : parsed.longitude;
            if (typeof lat === "number" && !isNaN(lat) && isFinite(lat)) latitude = lat;
            if (typeof lon === "number" && !isNaN(lon) && isFinite(lon)) longitude = lon;
            return {
                body: parsed.body,
                latitude,
                longitude,
            };
        }
    } catch {
        // not JSON — ignore
    }
    return null;
}

/**
 * Check if a message body is a valid SOS JSON payload.
 * @param rawBody The raw message body to check.
 * Returns true if the body can be parsed as SOS data, false otherwise.
 */
export function isSOSMatrixEvent(eventType: MatrixEvent): boolean {
    return isSOSMatrixEventByInnerInfo(eventType.getType(), eventType.getContent());
}

/**
 * Check if a message body is a valid SOS JSON payload.
 * @param rawBody The raw message body to check.
 * Returns true if the body can be parsed as SOS data, false otherwise.
 */
export function isSOSMatrixEventByInnerInfo(eventType: string, content: IContent): boolean {
    // Per adesso gli SOS sono supportati solo per VVF e solo come messaggi di testo
    return isVVF() && eventType === EventType.RoomMessage &&
        typeof content.body === "string" &&
        tryParseSOS(content.body) !== null;
}

/**
 * Custom SOS map marker: red circle with white "SOS" text.
 */
const SOSMapMarker: React.FC<{ map: maplibregl.Map; geoUri: string }> = memo(({ map, geoUri }) => {
    const markerRef = useRef<maplibregl.Marker | undefined>(undefined);

    const onElementRef = useCallback(
        (element: HTMLDivElement | null) => {
            if (!element) {
                markerRef.current?.remove();
                markerRef.current = undefined;
                return;
            }
            if (markerRef.current) return;
            const coords = parseGeoUri(geoUri);
            if (coords) {
                markerRef.current = createMarker(coords, element);
                markerRef.current.addTo(map);
            }
        },
        [geoUri, map],
    );

    return (
        <span>
            <div ref={onElementRef} className="mx_SOSMarker">
                <div className="mx_SOSMarker_circle">
                    <span className="mx_SOSMarker_text">SOS</span>
                </div>
            </div>
        </span>
    );
});

/**
 * Full-screen dialog for SOS messages, shown when the user clicks the map.
 */
const SOSLocationViewDialog: React.FC<{ geoUri: string; onFinished: () => void }> = ({ geoUri, onFinished }) => {
    const [error, setError] = useState<Error | undefined>();
    const dialogId = useMemo(() => `mx_SOSLocationViewDialog_${secureRandomString(8)}`, []);
    const onError = useCallback((e: Error) => setError(e), []);

    return (
        <BaseDialog className="mx_LocationViewDialog" onFinished={onFinished} fixedWidth={false}>
            {error ? (
                <div className="mx_EventTile_body mx_EventTile_tileError">{_t("location_sharing.failed_load_map")}</div>
            ) : (
                <Map
                    id={dialogId}
                    centerGeoUri={geoUri}
                    onError={onError}
                    interactive
                    className="mx_LocationViewDialog_map"
                    allowGeolocate
                >
                    {({ map }: { map: maplibregl.Map }) => (
                        <>
                            <SOSMapMarker map={map} geoUri={geoUri} />
                            <ZoomButtons map={map} />
                        </>
                    )}
                </Map>
            )}
        </BaseDialog>
    );
};

/**
 * Body component for SOS messages, compatible with IBodyProps.
 * Extracts SOS data from the mxEvent content body.
 */
const SOSMessageBody: React.FC<IBodyProps> = memo(({ mxEvent }): JSX.Element => {
    const matrixClient = useContext(MatrixClientContext);
    const content = mxEvent.getContent();

    // All hooks must be called before any early return (Rules of Hooks)
    const mapId = useMemo(() => `mx_SOSMessage_map_${secureRandomString(8)}`, []);

    // Memoize parsing so tryParseSOS doesn't run when body hasn't changed
    const sosData = useMemo(() => tryParseSOS(content.body ?? ""), [content.body]);

    // Memoize geoUri so its reference stays stable when coords haven't changed
    const geoUri = useMemo(() => {
        if (sosData?.latitude != null && sosData?.longitude != null) {
            return `geo:${sosData.latitude},${sosData.longitude}`;
        }
        return undefined;
    }, [sosData?.latitude, sosData?.longitude]);

    // Shared hook — error state + reconnect listener
    const { mapError, onMapError } = useLocationMapError(matrixClient);

    // Memoize the render prop so SOSMapMarker's memo is not bypassed by a new function reference
    const renderMarker = useCallback(
        ({ map }: { map: maplibregl.Map }) => geoUri ? <SOSMapMarker map={map} geoUri={geoUri} /> : null,
        [geoUri],
    );

    const onMapClick = useCallback(() => {
        if (!geoUri) return;
        Modal.createDialog(
            SOSLocationViewDialog,
            { geoUri },
            "mx_LocationViewDialog_wrapper",
            false, // isPriority
            true,  // isStatic
        );
    }, [geoUri]);

    // Fallback: shouldn't happen if MessageEvent validates correctly
    if (!sosData) return <></>;

    return (
        <div className={classNames({
            mx_SOSMessageBody: true,
            mx_SOSMessageBody_messageOnly: !geoUri,
        })}>
            <div className="mx_SOSMessageBody_header">
                <span className="mx_SOSMessageBody_headerText">{_t("timeline.m_sos.header")}</span>
                <hr className="mx_SOSMessageBody_headerDivider" />
                <span className="mx_SOSMessageBody_bodyText">{sosData.body}</span>
            </div>
            {geoUri && (
                <>
                    {mapError ? (
                        <div className="mx_EventTile_body mx_EventTile_tileError">
                            {_t("location_sharing.failed_load_map")}
                        </div>
                    ) : (
                        <LocationMapTile
                            mapId={mapId}
                            geoUri={geoUri}
                            tooltip={_t("location_sharing.expand_map")}
                            mapClassName="mx_SOSMessageBody_map"
                            containerClassName="mx_SOSMessageBody_mapContainer"
                            onClick={onMapClick}
                            onError={onMapError}
                            renderMarker={renderMarker}
                        />
                    )}
                    <div className="mx_SOSMessageBody_coords">
                        {sosData.latitude}, {sosData.longitude}
                    </div>
                </>
            )}
        </div>
    );
});

export default SOSMessageBody;
